import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KeyRound, LogOut, Truck, User, Package, MessageSquareWarning } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import NewCaseDialog from "@/components/cases/NewCaseDialog";

function StatusPill({ status }) {
  const isShipped = status === "shipped";
  const isPaid = status === "paid";
  const isRefunded = status === "refunded" || status === "partially_refunded";
  return (
    <span
      className={`inline-block font-mono text-[10px] uppercase tracking-editorial border px-2 py-0.5 ${
        isShipped ? "border-[#7A2021] text-[#7A2021]" :
        isPaid ? "border-[#1A1714] text-[#1A1714]" :
        isRefunded ? "border-[#1A1714]/40 text-[#1A1714]/60" :
        "border-[#1A1714]/40 text-[#1A1714]/60"
      }`}
    >
      {status || "—"}
    </span>
  );
}

export default function Account() {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const [caseOrder, setCaseOrder] = useState(null); // order for which we're opening a new case

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) setName(user.name || "");
  }, [user]);

  const loadOrders = () => {
    api.get("/orders/mine")
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  };

  useEffect(() => {
    if (!user) return;
    loadOrders();
  }, [user]);

  const saveName = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      toast.error("Name cannot be empty");
      return;
    }
    if (n === user.name) return;
    setSavingName(true);
    try {
      const { data } = await api.put("/auth/me", { name: n });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    } finally {
      setSavingName(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="pt-28 md:pt-40 max-w-[1440px] mx-auto px-6 md:px-10 pb-24">
        <p className="font-serif italic text-2xl text-[#1A1714]/60">Loading your account…</p>
      </div>
    );
  }

  return (
    <div className="pt-28 md:pt-40 max-w-4xl mx-auto px-6 md:px-10 pb-24" data-testid="account-page">
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Ch. 07 — Your shelf</p>
      <h1 className="font-serif text-3xl sm:text-4xl md:text-7xl tracking-tight leading-none text-[#1A1714] mt-3">
        My <em className="font-light">account</em>
      </h1>
      <p className="font-serif text-lg mt-5 text-[#1A1714]/70 max-w-xl">
        Update your profile, review your reading history, and keep your account secure — all in one place.
      </p>

      {/* --- Profile card --- */}
      <section className="mt-12 border border-[#E5E1D8] p-6 md:p-8" data-testid="account-profile-card">
        <div className="flex items-center gap-2 mb-4">
          <User size={16} className="text-[#7A2021]" />
          <h2 className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]">Profile</h2>
        </div>

        <form onSubmit={saveName} className="space-y-4 max-w-lg">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Email</label>
            <Input
              value={user.email}
              disabled
              className="mt-1 rounded-none border-[#E5E1D8] h-11 bg-[#F3EFE6] text-[#1A1714]/70"
              data-testid="account-email"
            />
            <p className="mt-1 font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/40">
              To change your email, please contact us.
            </p>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Full name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 rounded-none border-[#E5E1D8] h-11"
              data-testid="account-name-input"
            />
          </div>
          <Button
            type="submit"
            disabled={savingName || name.trim() === (user.name || "").trim()}
            data-testid="account-save-profile"
            className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-11 disabled:opacity-40"
          >
            {savingName ? "Saving…" : "Save changes"}
          </Button>
        </form>
      </section>

      {/* --- Security card --- */}
      <section className="mt-8 border border-[#E5E1D8] p-6 md:p-8" data-testid="account-security-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={16} className="text-[#7A2021]" />
          <h2 className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]">Security</h2>
        </div>
        <p className="font-serif text-lg text-[#1A1714]/80 mb-4 max-w-lg">
          Choose a password only you know. We store it hashed — even we can't read it.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setPwOpen(true)}
            data-testid="account-change-password-btn"
            variant="outline"
            className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial h-11"
          >
            <KeyRound size={13} className="mr-2" /> Change password
          </Button>
          <Button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="account-signout-btn"
            variant="outline"
            className="rounded-none border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-[#F9F6F0] font-mono text-xs uppercase tracking-editorial h-11"
          >
            <LogOut size={13} className="mr-2" /> Sign out
          </Button>
        </div>
      </section>

      {/* --- Orders --- */}
      <section className="mt-8" data-testid="account-orders-section">
        <div className="flex items-center gap-2 mb-4">
          <Package size={16} className="text-[#7A2021]" />
          <h2 className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]">Order history</h2>
        </div>

        {orders == null ? (
          <p className="font-serif italic text-lg text-[#1A1714]/60">Loading orders…</p>
        ) : orders.length === 0 ? (
          <div className="border border-[#E5E1D8] p-8 text-center" data-testid="account-orders-empty">
            <p className="font-serif text-xl text-[#1A1714]">Your reading history is just beginning.</p>
            <p className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/60 mt-2">
              No orders yet — the first chapter is waiting.
            </p>
            <Link
              to="/shop"
              className="inline-block mt-6 bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial px-6 py-3"
              data-testid="account-empty-shop-cta"
            >
              Browse the shop →
            </Link>
          </div>
        ) : (
          <div className="border border-[#E5E1D8]" data-testid="account-orders-list">
            {orders.map((o) => {
              const openCase = (o.cases || []).find((c) => c.is_open);
              const anyCase = (o.cases || [])[0];
              const canOpenCase = !openCase && !["cancelled"].includes(o.status);
              return (
                <div
                  key={o.order_id}
                  className="border-b border-[#E5E1D8] last:border-b-0 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
                  data-testid={`account-order-${o.order_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <p className="font-serif text-xl text-[#1A1714]">
                        Order <span className="text-[#7A2021]">#{o.order_id}</span>
                      </p>
                      <StatusPill status={o.status} />
                      {anyCase && (
                        <Link
                          to={`/cases/${anyCase.id}`}
                          data-testid={`account-case-pill-${o.order_id}`}
                          className={`inline-block font-mono text-[10px] uppercase tracking-editorial border px-2 py-0.5 ${
                            anyCase.is_open ? "border-[#7A2021] text-[#7A2021]" : "border-[#1A1714]/40 text-[#1A1714]/60"
                          } hover:opacity-80`}
                        >
                          Case #{anyCase.case_number} · {anyCase.status}
                        </Link>
                      )}
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mt-1">
                      {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
                      {" · "}
                      {o.item_count} {o.item_count === 1 ? "item" : "items"}
                      {" · $"}{Number(o.amount || 0).toFixed(2)}
                    </p>
                    {o.titles && o.titles.length > 0 && (
                      <p className="mt-1 font-serif italic text-sm text-[#1A1714]/70 truncate">
                        {o.titles.join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {o.tracking_number && (
                      <Link
                        to={`/track/${o.order_id}?email=${encodeURIComponent(user.email)}`}
                        data-testid={`account-track-${o.order_id}`}
                        className="inline-flex items-center gap-2 border border-[#1A1714] hover:bg-[#1A1714] hover:text-[#F9F6F0] font-mono text-[10px] uppercase tracking-editorial px-4 py-2 transition-colors"
                      >
                        <Truck size={12} /> Track order
                      </Link>
                    )}
                    {canOpenCase && (
                      <button
                        onClick={() => setCaseOrder(o)}
                        data-testid={`account-open-case-${o.order_id}`}
                        className="inline-flex items-center gap-2 border border-[#7A2021] text-[#7A2021] hover:bg-[#7A2021] hover:text-[#F9F6F0] font-mono text-[10px] uppercase tracking-editorial px-4 py-2 transition-colors"
                      >
                        <MessageSquareWarning size={12} /> Open a case
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <NewCaseDialog
        open={!!caseOrder}
        order={caseOrder}
        onOpenChange={(o) => { if (!o) setCaseOrder(null); }}
        onCreated={() => { setCaseOrder(null); loadOrders(); }}
      />
    </div>
  );
}
